import { CommodityCatalogueDto } from '@billing/app/api'
import { useBillingServiceStore } from '@billing/AppProvider'
import { CommoditiesNestedTable } from '@billing/features/commodity/components/organisms/CommoditiesNestedTable'
import { Button, Container, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { OptionsButton, PlusIcon, useMinimalsTheme } from '@tom-ui/ui'
import { downloadFile, PATHS } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useNavigate } from 'react-router'
import { CommodityFilterUIStore } from '../stores/commodity-filter.ui-store'
import { CommodityListUIStore } from '../stores/commodity-list.ui-store'

interface Props {
  listStore: CommodityListUIStore
  filterStore: CommodityFilterUIStore
}
export const CommoditiesContainer = observer(({ listStore, filterStore }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const navigate = useNavigate()

  const { catalogueStore, catalogueDialogUIStore, commodityDialogUIStore } =
    useBillingServiceStore()

  const handleChangeCatalogue = (id: number) => {
    catalogueStore.setSelectedCatalogueId(id)
  }

  const triggerCreateDialog = (catalogue?: CommodityCatalogueDto) => {
    catalogueDialogUIStore.toggleCreate(catalogue)
  }

  const triggerDeleteDialog = (catalogue?: CommodityCatalogueDto) => {
    catalogueDialogUIStore.toggleDelete(catalogue)
  }

  const handleExport = async (catalogueName: string) => {
    const exportCsvData = await listStore.getNestedCommoditiesToCsv()
    downloadFile(new Blob([exportCsvData as BlobPart]), `${catalogueName}.csv`)
  }

  const handleImport = () => {
    catalogueDialogUIStore.toggleImport()
  }

  const handleCreateNewCommodity = (catalogue?: CommodityCatalogueDto) => {
    navigate(`${PATHS.masterData.commoditiesAndPackaging.commodity.url}/${catalogue?.id}/null/new`)
  }

  return (
    <Container sx={{ pb: theme.customSpacing.m }}>
      <Stack
        direction='row'
        alignItems='center'
        gap={1}
        padding={theme.customSpacing.xs}
        paddingX={theme.customSpacing.m}
        sx={{
          backgroundColor: theme.palette.grey[300],
        }}
      >
        {catalogueStore?.items?.map(catalogue => {
          const isSelected = catalogueStore.selectedCatalogueId === catalogue.id
          return (
            <Button
              key={`catalogue_${catalogue.id}`}
              variant='contained'
              size='small'
              color={isSelected ? 'secondary' : 'inherit'}
              onClick={() => handleChangeCatalogue(catalogue.id)}
            >
              <Stack direction='row' gap={1}>
                <Typography variant='body2'>{t(catalogue.name)}</Typography>
                <Typography variant='body2' fontWeight={700}>
                  {catalogueDialogUIStore.catalogueQuantityMap.get(catalogue.id)}
                </Typography>

                {isSelected && (
                  <OptionsButton
                    size='small'
                    position='bottom-start'
                    fill={theme.palette.common.white}
                    noPadding
                    options={[
                      {
                        label: t('rename', 'Rename'),
                        onClick: () => triggerCreateDialog(catalogue),
                      },
                      {
                        label: t('addNewCommodity', 'Add new commodity'),
                        onClick: () => handleCreateNewCommodity(catalogue),
                      },
                      {
                        label: t('exportCsv', 'Export csv'),
                        onClick: () => handleExport(catalogue.name),
                      },
                      {
                        label: t('importCsv', 'Import csv'),
                        onClick: () => handleImport(),
                      },
                      {
                        label: t('delete', 'Delete'),
                        onClick: () => triggerDeleteDialog(catalogue),
                      },
                    ]}
                  />
                )}
              </Stack>
            </Button>
          )
        })}
        <Button size='small' color='secondary' onClick={() => triggerCreateDialog()}>
          <PlusIcon fontSize='small' sx={{ marginRight: '0.5rem' }} />
          {t('addCatalogue', 'Add catalogue')}
        </Button>
      </Stack>
      <CommoditiesNestedTable
        listStore={listStore}
        filterStore={filterStore}
        dialogStore={commodityDialogUIStore}
      />
    </Container>
  )
})
